import { AlertTriangle } from 'lucide-react'
import { Button } from './button'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogDescription,
} from './dialog'

interface ConfirmDialogProps {
  open: boolean
  title?: string
  description?: string
  /** Prominent warning surfaced as a highlighted alarm callout below the
   *  description. Use for consequences the operator must not miss (e.g. a
   *  published playlist keeps showing on screens after it's deleted). */
  warning?: string
  /** Optional lines listed under the warning text (e.g. each screen still
   *  showing the content as "Screen — Branch"). Rendered as a compact list
   *  inside the same alarm callout. */
  warningItems?: string[]
  confirmLabel?: string
  onConfirm: () => void
  onClose: () => void
}

export function ConfirmDialog({
  open,
  title = 'Are you sure?',
  description = 'This action cannot be undone.',
  warning,
  warningItems,
  confirmLabel = 'Delete',
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-full bg-destructive/10 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-4 h-4 text-destructive" />
            </div>
            <DialogTitle className="text-base">{title}</DialogTitle>
          </div>
          <DialogDescription className="ps-12">{description}</DialogDescription>
        </DialogHeader>
        {(warning || (warningItems && warningItems.length > 0)) && (
          <div className="ms-12 -mt-1 flex min-w-0 items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div className="min-w-0">
              {warning && <span>{warning}</span>}
              {warningItems && warningItems.length > 0 && (
                <ul className="mt-1.5 space-y-0.5 font-medium">
                  {warningItems.map((item, i) => (
                    <li key={i} className="truncate">{item}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-2 mt-1">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            variant="destructive"
            onClick={() => { onConfirm(); onClose() }}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
